import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import axios from 'axios';
import InputText from '../Form/Fields/InputText';
import InputDate from '../Form/Fields/InputDate';
import Switcher from '../Form/Fields/Switcher';
import InputPhoneIntl from '../Form/Fields/InputPhoneIntl';
import Select from '../Form/Fields/Select';
import InputInsurance from '../Form/Fields/InputInsurance';
import Button from '../Form/Button';
import Accordion from '../NavTab/Accordion';
import InlineLoader from '../Loader/InlineLoader';
import { getRegimeOptions } from '../../config/Regime';
import Form from '../Form/Form';
import { toast } from 'react-toastify';
import { faUser, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import './PatientInformation.scss';
import { API_BASE_URL } from '../../api';

// Label above component — keeps field components completely untouched
const F = ({ label, component: Component, name, full = false, ...rest }) => (
    <div className={full ? 'pi-full' : undefined}>
        <label className="pi-field-label">{label}</label>
        <Component name={name} {...rest} />
    </div>
);

// Only renders the complementary insurance fields when "Complémentaire ?" is ON
const ComplementaryFields = () => {
    const { watch } = useFormContext();
    const hasComplementary = watch('hasComplementary');

    if (!hasComplementary) return null;

    return (
        <>
            <F label="Mutuelle"    component={InputInsurance}     name="healthInsurance" />
            <F label="N° adhérent mutuelle" component={InputText}   name="complementaryNumber" />
            <F label="Validité couverture" component={InputDate} name="insuranceDateLimit" />
        </>
    );
};

const PatientInformation = () => {
    const { id } = useParams();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    const handleSubmit = (data) => {
        setFormLoading(true);
        const method = patient?.id ? 'put' : 'post';
        const url = patient?.id
            ? `${API_BASE_URL}/api/v1/updatePatient/${patient.id}`
            : `${API_BASE_URL}/api/v1/createPatient`;

        // the photo is set from the avatar on the overview tab, not from this form
        const formData = new FormData();
        const { photo, ...patientData } = data;
        formData.append('patient', new Blob([JSON.stringify(patientData)], { type: 'application/json' }));

        axios({ method, url, data: formData, withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } })
            .then(() => toast.success('Patient sauvegardé avec succès !'))
            .catch(() => toast.error('Erreur lors de la sauvegarde'))
            .finally(() => setFormLoading(false));
    };

    useEffect(() => {
        const fetchPatientData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE_URL}/api/v1/getPatient/${id}`, { withCredentials: true });
                setPatient(response.data);
            } catch (err) {
                setError('Erreur lors de la récupération des données du patient.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchPatientData();
    }, [id]);

    if (loading)  return <InlineLoader />;
    if (error)    return <div>{error}</div>;
    if (!patient) return <div>Aucun patient trouvé.</div>;

    return (
            <Form id="updatePatientForm" className="pi" onSubmit={handleSubmit} defaultValues={patient}>

                {/* ── Identité ── */}
                <Accordion id="patientIdentity" parent="patient_information_container" title="Identité" icon={faUser}>
                    <div className="pi-grid">
                        <F label="Prénom"            component={InputText}  name="firstName"   placeholder="Prénom" />
                        <F label="Nom"               component={InputText}  name="lastName"    placeholder="Nom" />
                        <F label="Sexe"              component={Select}     name="sex"         options={[{ value: 0, label: 'Homme' }, { value: 1, label: 'Femme' }]} />
                        <F label="Date de naissance" component={InputDate}  name="birthDate" />
                        <F label="Email"             component={InputText}  name="email"       placeholder="Email" />
                        <F label="Téléphone"         component={InputPhoneIntl} name="phoneNumber" placeholder="Téléphone" />
                        <F label="CIN"               component={InputText}  name="cin" />
                    </div>
                </Accordion>

                {/* ── Sécurité sociale & assurance ── */}
                <Accordion id="patientInsurance" parent="patient_information_container" title="Sécurité sociale & assurance" icon={faShieldAlt}>
                    <div className="pi-grid">
                        <F label="Activité"    component={Select}    name="activity"    options={[{ value: 0, label: 'Retraité' }, { value: 1, label: 'Salarié' }]} />
                        <F label="Régime SS"   component={Select}    name="regime" options={getRegimeOptions().filter(option => option.value !== '')} />
                        <F label="N° SS"       component={InputText} name="socialSecurityNumber" />
                        <F label="Complémentaire ?" component={Switcher}      name="hasComplementary" full />
                        <ComplementaryFields />
                    </div>
                </Accordion>

                <div className="pi-footer">
                    <Button
                        type="submit"
                        form="updatePatientForm"
                        className="btn btn-primary fw-bold"
                        isLoading={formLoading}
                        loadingMessage="Enregistrement…"
                    >
                        Enregistrer
                    </Button>
                </div>

            </Form>
    );
};

export default PatientInformation;
